import { useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { getNativePlatform } from '#features/notifications/utils/nativePlatform';
import { envConfig } from '#constants/envConfig';
import {
  GUARDED_NAVIGATION_EVENT,
  runApprovedNavigation,
  type GuardedNavigation,
} from '../model/guardedNavigation';
import { hasIosBackSelection, iosBackExcludedAreas } from '../model/iosBackInteractions';
import { canPreviewIosBack, iosBackPreviewTarget, type BackScreen } from '../model/iosBackPreview';
import {
  isNativeBackRoot,
  nativeBackFallback,
  nativeBackFormState,
  nativeBackOverlay,
  nativeBackPath,
} from '../model/nativeBackForms';
import { hasNativeBackHandler, runNativeBackHandler } from '../model/nativeBackStack';
import { iosNavigation } from '../native/iosNavigation';

/** UIKit owns the movement; the SPA owns interaction exclusions, drafts and route history. */
export const useNativeIosBack = () => {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const pendingRef = useRef<(() => void) | null>(null);
  const cancel = () => {
    pendingRef.current = null;
    setPending(false);
  };

  useEffect(() => {
    if (!confirmed) return;
    const action = pendingRef.current;
    pendingRef.current = null;
    setConfirmed(false);
    action?.();
  }, [confirmed]);

  useEffect(() => {
    const request = (event: Event) => {
      event.preventDefault();
      const { action, scope, protectDirty } = (event as CustomEvent<GuardedNavigation>).detail;
      const state = nativeBackFormState(scope);
      // The clean confirmation itself must remain dismissible via Escape/X/outside.
      if (pendingRef.current && state !== 'ready') return;
      if (state === 'busy') return;
      const origin = router.state.location.href;
      const guarded = () => {
        if (
          scope.isConnected &&
          router.state.location.href === origin &&
          nativeBackFormState(scope) !== 'busy'
        )
          runApprovedNavigation(action, scope);
      };
      if (protectDirty && state === 'dirty') {
        pendingRef.current = guarded;
        setPending(true);
      } else guarded();
    };
    window.addEventListener(GUARDED_NAVIGATION_EVENT, request);
    return () => window.removeEventListener(GUARDED_NAVIGATION_EVENT, request);
  }, [router]);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) return;
    let active = true;
    let remove: (() => void) | undefined;
    let lastBackAt = -Infinity;
    const previousPaths = new Map<number, string>();
    const screens = new Map<number, BackScreen>();
    let gesture:
      | { id: string; currentKey: string; targetKey: string; beginning: Promise<void> }
      | undefined;
    let finishing = false;
    let committing = false;
    let suspendedKey: string | undefined;
    const screen = (): BackScreen => {
      const location = router.history.location;
      return {
        key: location.state.__TSR_key ?? `${location.state.__TSR_index}:${location.pathname}`,
        href: location.href ?? location.pathname,
        pathname: location.pathname,
        index: location.state.__TSR_index,
      };
    };
    const remember = () => {
      if (suspendedKey !== undefined) return;
      previousPaths.set(
        router.history.location.state.__TSR_index,
        router.history.location.pathname
      );
      const current = screen();
      screens.set(current.index, current);
      // Keep enough metadata for a parent lookup, without retaining an unbounded history.
      if (screens.size > 20) screens.delete(screens.keys().next().value!);
    };
    remember();
    const unsubscribe = router.history.subscribe(remember);

    const eligible = () =>
      canPreviewIosBack({
        overlay: !!nativeBackOverlay(),
        formState: nativeBackFormState(document.body),
        pending: !!pendingRef.current,
        keyboard:
          document.activeElement instanceof HTMLElement &&
          document.activeElement.matches('input, textarea, [contenteditable="true"]'),
        root: !hasNativeBackHandler() && isNativeBackRoot(router.state.location.pathname),
      });
    const target = () => {
      const current = screen();
      const parent = hasNativeBackHandler()
        ? /^\/dashboard\/chats(?:\/|$)/.test(nativeBackPath(current.pathname))
          ? '/dashboard/chats'
          : undefined
        : !router.history.canGoBack()
          ? nativeBackPath(nativeBackFallback(current.pathname).replace('/{-$locale}', ''))
          : undefined;
      if (hasNativeBackHandler() && !parent) return undefined;
      return iosBackPreviewTarget(screens, current, router.history.canGoBack(), parent);
    };
    let preparedSignature = '';
    let geometryTimer: ReturnType<typeof setTimeout> | undefined;
    let cachedAreas: ReturnType<typeof iosBackExcludedAreas> | undefined;
    const syncInteractions = () => {
      if (!active || geometryTimer !== undefined || document.visibilityState === 'hidden') return;
      cachedAreas ??= iosBackExcludedAreas();
      const available =
        suspendedKey === undefined &&
        !pendingRef.current &&
        !hasIosBackSelection() &&
        !nativeBackOverlay() &&
        nativeBackFormState(document.body) !== 'busy' &&
        !document.activeElement?.matches('input, textarea, [contenteditable="true"]') &&
        (hasNativeBackHandler() || !isNativeBackRoot(router.state.location.pathname));
      void iosNavigation.interactions({
        key: screen().key,
        enabled: available,
        content: available && eligible() && !!target(),
        selected: hasIosBackSelection(),
        valid: true,
        areas: cachedAreas,
      });
    };
    const scheduleInteractions = () => {
      clearTimeout(geometryTimer);
      cachedAreas = undefined;
      // Scroll/resize invalidates cached hit areas immediately; only re-enable after layout.
      void iosNavigation.interactions({
        key: screen().key,
        enabled: false,
        content: false,
        selected: hasIosBackSelection(),
        valid: false,
        areas: [],
      });
      geometryTimer = setTimeout(() => {
        geometryTimer = undefined;
        syncInteractions();
      }, 80);
    };
    const prepare = () => {
      if (!active) return;
      const destination = suspendedKey === undefined && eligible() ? target() : undefined;
      const plan = {
        currentKey: screen().key,
        targetKey: destination?.key ?? '',
        targetHref: destination?.href ?? '',
      };
      const signature = JSON.stringify(plan);
      if (signature === preparedSignature) return;
      preparedSignature = signature;
      void iosNavigation.prepare(plan);
    };
    const start = (event: Event) => {
      const id = (event as CustomEvent<{ id?: string }>).detail?.id;
      gesture = undefined;
      if (!id || suspendedKey !== undefined || !eligible()) return;
      const destination = target();
      if (!destination) return;
      const plan = { id, currentKey: screen().key, targetKey: destination.key };
      gesture = {
        ...plan,
        beginning: iosNavigation.begin({ ...plan, targetHref: destination.href }),
      };
    };
    const finishBack = async (event: Event) => {
      const id = (event as CustomEvent<{ id?: string }>).detail?.id;
      const preview = gesture && gesture.id === id ? gesture : undefined;
      gesture = undefined;
      const restore = () => {
        if (id) void iosNavigation.complete(id, false);
      };
      // UIKit may have emitted end before JS delivered begin. Preserve bridge ordering
      // and recheck the current route/forms after the acknowledgement, not before it.
      if (preview) await preview.beginning;
      if (!active || suspendedKey !== undefined) {
        restore();
        return;
      }
      if (preview && (preview.currentKey !== screen().key || preview.targetKey !== target()?.key)) {
        restore();
        return;
      }
      if (pendingRef.current) {
        restore();
        pendingRef.current = null;
        setPending(false);
        return;
      }
      if (Date.now() - lastBackAt < 350) {
        restore();
        return;
      }
      lastBackAt = Date.now();
      const overlay = nativeBackOverlay();
      const path = router.state.location.pathname;
      if (!overlay && !hasNativeBackHandler() && isNativeBackRoot(path)) {
        restore();
        return;
      }
      const scope = overlay?.formBoundary ? overlay.node : document.body;
      const state = nativeBackFormState(scope);
      const previousStep = !overlay && !!document.querySelector('[data-native-back-step="true"]');
      const formState =
        (overlay && !overlay.formBoundary) || (previousStep && state === 'dirty') ? 'ready' : state;
      if (formState === 'busy') {
        restore();
        return;
      }

      const action = () => {
        if ((!overlay || overlay.formBoundary) && nativeBackFormState(scope) === 'busy') return;
        if (overlay && overlay.node.isConnected) {
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
          return;
        }
        if (runNativeBackHandler()) return;
        if (isNativeBackRoot(router.state.location.pathname)) return;
        const previous = previousPaths.get(router.history.location.state.__TSR_index - 1);
        const previousWasAuth =
          previous && /\/(sign-in|sign-up|forgot-password|reset-password)(\/|$)/.test(previous);
        if (router.history.canGoBack() && !previousWasAuth) router.history.back();
        else
          void router.navigate({
            to: nativeBackFallback(router.state.location.pathname),
            replace: true,
          });
      };
      if (formState === 'dirty') {
        restore();
        pendingRef.current = () => runApprovedNavigation(action, scope);
        setPending(true);
      } else {
        committing = !!preview && eligible();
        if (id && committing) {
          await iosNavigation.complete(id, true);
          if (
            !active ||
            suspendedKey !== undefined ||
            preview?.currentKey !== screen().key ||
            preview?.targetKey !== target()?.key ||
            !eligible()
          ) {
            committing = false;
            void iosNavigation.complete(id, false);
            return;
          }
        } else if (id) void iosNavigation.complete(id, false);
        action();
      }
    };
    const back = (event: Event) => {
      if (finishing) return;
      finishing = !!gesture && gesture.id === (event as CustomEvent<{ id?: string }>).detail?.id;
      void finishBack(event).finally(() => {
        finishing = false;
        prepare();
      });
    };
    void (async () => {
      const platform = await getNativePlatform();
      if (!active || platform !== 'ios') return;
      void iosNavigation.reset();
      const rendered = () => {
        cachedAreas = undefined;
        if (suspendedKey !== undefined) {
          if (screen().key === suspendedKey) return;
          suspendedKey = undefined;
        }
        remember();
        committing = false;
        const current = screen();
        void iosNavigation.rendered(current.key, current.href);
        preparedSignature = '';
        prepare();
        syncInteractions();
      };
      rendered();
      const unrender = router.subscribe('onRendered', rendered);
      const unblock = router.history.block({
        enableBeforeUnload: false,
        blockerFn: async () => {
          const current = screen();
          if (
            suspendedKey === undefined &&
            !committing &&
            !nativeBackOverlay() &&
            nativeBackFormState(document.body) === 'ready' &&
            !document.activeElement?.matches('input, textarea, [contenteditable="true"]') &&
            !/\/(sign-in|sign-up|forgot-password|reset-password)(\/|$)/.test(current.pathname)
          )
            await iosNavigation.capture(current.key);
          return false;
        },
      });
      const clear = () => {
        gesture = undefined;
        preparedSignature = '';
        void iosNavigation.reset();
      };
      const identityReset = () => {
        suspendedKey = screen().key;
        screens.clear();
        previousPaths.clear();
        committing = false;
        pendingRef.current = null;
        setPending(false);
        clear();
      };
      window.addEventListener('teamplay:ios-navigation-identity-reset', identityReset);
      const theme = new MutationObserver(() => {
        clear();
        rendered();
      });
      theme.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme'],
      });
      const visibility = () => {
        if (document.visibilityState === 'hidden') clear();
        else rendered();
      };
      // Only semantic guards/overlay mounting and focus affect a prepared preview.
      // Deduplicate the plan, so unrelated page mutations never flood the bridge.
      const guards = new MutationObserver(() => {
        prepare();
        scheduleInteractions();
      });
      guards.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'data-native-back-dirty',
          'data-native-back-busy',
          'data-state',
          'hidden',
          'class',
          'style',
          'data-native-gesture-block',
        ],
      });
      const focusChanged = () =>
        queueMicrotask(() => {
          prepare();
          syncInteractions();
        });
      document.addEventListener('focusin', prepare);
      document.addEventListener('focusin', syncInteractions);
      document.addEventListener('focusout', focusChanged);
      document.addEventListener('selectionchange', syncInteractions);
      document.addEventListener('scroll', scheduleInteractions, true);
      window.addEventListener('resize', scheduleInteractions);
      const heartbeat = setInterval(syncInteractions, 800);
      window.addEventListener('teamplay:native-back-handler-change', prepare);
      document.addEventListener('visibilitychange', visibility);
      window.addEventListener('teamplay:ios-back-start', start);
      window.addEventListener('teamplay:ios-back', back);
      remove = () => {
        window.removeEventListener('teamplay:ios-navigation-identity-reset', identityReset);
        window.removeEventListener('teamplay:ios-back', back);
        window.removeEventListener('teamplay:ios-back-start', start);
        document.removeEventListener('visibilitychange', visibility);
        document.removeEventListener('focusin', prepare);
        document.removeEventListener('focusin', syncInteractions);
        document.removeEventListener('focusout', focusChanged);
        document.removeEventListener('selectionchange', syncInteractions);
        document.removeEventListener('scroll', scheduleInteractions, true);
        window.removeEventListener('resize', scheduleInteractions);
        clearTimeout(geometryTimer);
        clearInterval(heartbeat);
        window.removeEventListener('teamplay:native-back-handler-change', prepare);
        guards.disconnect();
        theme.disconnect();
        unrender();
        unblock();
        clear();
      };
    })();
    return () => {
      active = false;
      remove?.();
      unsubscribe();
      pendingRef.current = null;
    };
  }, [router]);

  return {
    pending,
    cancel,
    confirm: () => {
      setPending(false);
      setConfirmed(true);
    },
  };
};
